import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Divider, Button } from '@mui/material';
import healthService from '../../../services/healthService';
import httpService from '../../../services/httpService';
import * as userSlice from '../../../data/user.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useUserType } from '../../../hooks/useUserType';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'About';

const classes = {
  container: `${PREFIX}-container`,
  separator: `${PREFIX}-separator`,
  button: `${PREFIX}-button`,
  version: `${PREFIX}-version`,
  label: `${PREFIX}-label`,
  actions: `${PREFIX}-actions`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },

  [`& .${classes.separator}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.button}`]: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.version}`]: {
    cursor: 'pointer',
  },

  [`& .${classes.label}`]: {
    fontWeight: 'bold',
  },

  [`& .${classes.actions}`]: {
    paddingTop: theme.spacing(2),
  },
}));

const convertToSimpleVersion = (version: string) => {
  const numbers = version.split('.');

  return `${numbers[0]}.${numbers[1]}`;
};

export default function About() {
  const { __environment__ } = window as any;
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [version, setVersion] = React.useState<string>('');
  const [showFullAppInfo, setShowFullAppInfo] = React.useState<boolean>(false);
  const [apiVersions, setApiVersions] = React.useState<Record<string, string>>(
    {},
  );

  const user = useAppSelector(userSlice.selectUserInfo);
  const applicationSettings = useAppSelector(
    referenceSlice.selectApplicationSettings,
  );
  const userType = useUserType(user);

  const done = (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    navigate(-1);
    evt.preventDefault();
  };

  React.useEffect(() => {
    healthService.healthCheck().subscribe({
      next(res) {
        setVersion(res.version);
      },
      error(err) {
        setVersion('API not available');
      },
    });
  }, []);

  const setApiVersion = React.useCallback((key: string, value: string) => {
    setApiVersions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const getApiBaseFromResourceUrl = (url?: string): string | null => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    const idx = trimmed.lastIndexOf('/');
    return idx > -1 ? trimmed.substring(0, idx) : trimmed;
  };

  const toHttpFromWs = (url?: string): string | undefined => {
    if (!url || typeof url !== 'string') return undefined;
    return url
      .trim()
      .replace(/^wss:\/\//i, 'https://')
      .replace(/^ws:\/\//i, 'http://');
  };

  const normalizeCommCenterUrl = (url?: string): string | undefined => {
    const httpUrl = toHttpFromWs(url);
    if (!httpUrl) return undefined;
    return httpUrl.replace(
      /communications-center-ws/gi,
      'communications-center',
    );
  };

  const fetchHealth = useCallback(
    (label: string, baseUrl: string | null, path = '/health-check') => {
      if (!baseUrl) return setApiVersion(label, 'N/A');

      setApiVersion(label, '...');
      const url = `${baseUrl}${path}`;

      const sub = httpService.getJSON(url).subscribe({
        next: (res) => {
          const ver = res?.version || res?.data?.version || 'unknown';
          setApiVersion(label, ver);
        },
        error: () => setApiVersion(label, 'N/A'),
      });

      return () => sub.unsubscribe?.();
    },
    [setApiVersion],
  );

  const fetchAllApiVersions = React.useCallback(() => {
    const env = __environment__ || {};

    const transportBase =
      getApiBaseFromResourceUrl(env.REACT_APP_PROVIDER_API_URL) ||
      getApiBaseFromResourceUrl(env.REACT_APP_DRIVER_API_URL) ||
      getApiBaseFromResourceUrl(env.REACT_APP_VEHICLE_API_URL) ||
      getApiBaseFromResourceUrl(env.REACT_APP_TRIP_API_URL);

    const lookupsBase = getApiBaseFromResourceUrl(env.REACT_APP_LOOKUP_API_URL);
    const userAuthBase = getApiBaseFromResourceUrl(env.REACT_APP_USER_API_URL);
    const commCenterBase = getApiBaseFromResourceUrl(
      normalizeCommCenterUrl(env.REACT_APP_PUSH_NOTIFICATION_ENDPOINT),
    );
    const f1PassengerBase = getApiBaseFromResourceUrl(
      env.REACT_APP_PASSENGER_API_URL,
    );

    fetchHealth('Transport API', transportBase);
    fetchHealth('Lookups API', lookupsBase);
    fetchHealth('User-auth API', userAuthBase);
    fetchHealth('CommCenter', commCenterBase);
    // f1cloud microservices use /health
    fetchHealth('f1cloud Passenger', f1PassengerBase, '/health');
  }, [__environment__, fetchHealth]);

  React.useEffect(() => {
    if (showFullAppInfo) {
      fetchAllApiVersions();
    }
  }, [showFullAppInfo, fetchAllApiVersions]);

  return (
    <StyledPaper className={classes.container}>
      {showFullAppInfo && (
        <>
          <Box className={classes.label}>{user?.emailAddress}</Box>
          <Box>
            <Box component="span" className={classes.label}>
              {t('about.groups')}
              {': '}
            </Box>
            {user?.access?.userRoles?.join(', ')}
          </Box>
          <Box>
            <Box component="span" className={classes.label}>
              {t('about.permissions')}
              {': '}
            </Box>
            {user?.access?.permissions
              ?.map(
                (p: { resource: string; action: string; scope: string }) =>
                  `${p.resource}.${p.action}.${p.scope}`,
              )
              .join(', ')}
          </Box>
          <Box>
            <Box component="span" className={classes.label}>
              {t('about.type')}
              {': '}
            </Box>
            {userType}
          </Box>
          <Divider className={classes.separator} />
          <Box>
            <Box component="span" className={classes.label}>
              {t('about.featureFlags')}
              {': '}
            </Box>
          </Box>
          <Box component="div">
            {Array.isArray(user?.featureFlags) &&
            user.featureFlags.length > 0 ? (
              user.featureFlags.map((flag: string) => {
                const setting = applicationSettings.find(
                  (s) => s.applicationSettingBC === flag,
                );
                return (
                  <Box key={flag} component="div">
                    {setting?.applicationSettingName ?? flag}
                    {` (${flag}) - ${
                      setting?.description ??
                      t('about.featureFlagDescriptionUnknown')
                    }`}
                  </Box>
                );
              })
            ) : (
              <Box component="div">{t('about.noFeatureFlags')}</Box>
            )}
          </Box>
          <Divider className={classes.separator} />
          <Box component="div">
            REACT_APP_USER_API_URL: {__environment__.REACT_APP_USER_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_LOOKUP_API_URL: {__environment__.REACT_APP_LOOKUP_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_PROVIDER_API_URL:{' '}
            {__environment__.REACT_APP_PROVIDER_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_DRIVER_API_URL: {__environment__.REACT_APP_DRIVER_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_VEHICLE_API_URL:{' '}
            {__environment__.REACT_APP_VEHICLE_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_TRIP_API_URL: {__environment__.REACT_APP_TRIP_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_NOTIFICATION_API_URL:{' '}
            {__environment__.REACT_APP_NOTIFICATION_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_TRIP_LEG_PACKAGE_API_URL:{' '}
            {__environment__.REACT_APP_TRIP_LEG_PACKAGE_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_ACCOUNTING_API_URL:{' '}
            {__environment__.REACT_APP_ACCOUNTING_API_URL}
          </Box>
          <Box component="div">
            REACT_APP_PUSH_NOTIFICATION_ENDPOINT:{' '}
            {__environment__.REACT_APP_PUSH_NOTIFICATION_ENDPOINT}
          </Box>
          <Divider className={classes.separator} />
        </>
      )}
      <Box
        className={classes.version}
        onClick={() => {
          setShowFullAppInfo(true);
        }}>
        <Box component="span" className={classes.label}>
          {t('about.appVersion')}
          {': '}
        </Box>
        {showFullAppInfo
          ? __environment__.REACT_APP_VERSION
          : convertToSimpleVersion(__environment__.REACT_APP_VERSION)}
      </Box>
      {showFullAppInfo && (
        <>
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.apiVersion')}
              {': '}
            </Box>
            {version}
          </Box>
          <Divider className={classes.separator} />
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.transportApi')}
              {': '}
            </Box>
            {apiVersions['Transport API'] ?? ''}
          </Box>
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.lookupsApi')}
              {': '}
            </Box>
            {apiVersions['Lookups API'] ?? ''}
          </Box>
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.userAuthApi')}
              {': '}
            </Box>
            {apiVersions['User-auth API'] ?? ''}
          </Box>
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.commCenter')}
              {': '}
            </Box>
            {apiVersions['CommCenter'] ?? ''}
          </Box>
          <Box className={classes.version}>
            <Box component="span" className={classes.label}>
              {t('about.f1Passenger')}
              {': '}
            </Box>
            {apiVersions['f1cloud Passenger'] ?? ''}
          </Box>
        </>
      )}
      <Divider className={classes.separator} />
      <Box className={classes.actions}>
        <Button href="#" color="primary" variant="contained" onClick={done}>
          {t('about.done')}
        </Button>
      </Box>
    </StyledPaper>
  );
}
